import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { getRequest } from '../../api/ApiController';
import { getChecklistGraphStatusColors, getChecklistGraphVyjadreniaColors } from '../../utils/moduleSettings';
import { Chart } from 'primereact/chart';
import { Calendar } from '../../components/PrimeCalendar';
import { Skeleton } from 'primereact/skeleton';
import { addLocale } from 'primereact/api';
import { RefreshIcon } from '../svg/svgIcons';
import { LocaleContext } from '../../components/LocaleContext';
import {
    buildPreviewPeriodOptionLabels,
    registerPreviewCalendarLocale,
} from '../../utils/previewUiLocale';

// Checklist preview historically persists the "all" period as raw token "Všetky" (not "Všetko").
const PERIOD_OPTIONS = ['Všetky', 'Za deň', 'Za mesiac', 'Za rok', 'Vlastné'];
const PREVIEW_PAGE = 'ChecklistPreview';

function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseSavedPeriod(attrs) {
  if (!attrs?.preview_period || typeof attrs.preview_period.timePeriod !== 'string') return null;
  if (!PERIOD_OPTIONS.includes(attrs.preview_period.timePeriod)) return null;
  const { timePeriod } = attrs.preview_period;
  const date = attrs.preview_period.date;
  if (timePeriod === 'Všetky') return { timePeriod, date: null };
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    return { timePeriod, date: date.map((d) => (d != null ? new Date(d) : null)) };
  }
  if (date != null) {
    const d = new Date(date);
    if (!isNaN(d.getTime())) return { timePeriod, date: d };
  }
  return null;
}

function serializePeriod(timePeriod, date) {
  const payload = { timePeriod };
  if (date == null) payload.date = null;
  else if (Array.isArray(date)) payload.date = date.map((d) => (d instanceof Date ? toLocalDateString(d) : d));
  else payload.date = date instanceof Date ? toLocalDateString(date) : date;
  return payload;
}

function getYesterdayISO() {
  const t = new Date();
  t.setDate(t.getDate() - 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function getDateRange(timePeriod, date) {
  if (!date) return { from: null, to: null };
  const lastDayStr = getYesterdayISO();
  let from, to;
  if (timePeriod === 'Za deň') {
    const d = new Date(date);
    const s = toLocalDateString(d);
    from = s;
    to = s;
  } else if (timePeriod === 'Za mesiac') {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
    from = `${y}-${m}-01`;
    to = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
  } else if (timePeriod === 'Za rok') {
    const y = new Date(date).getFullYear();
    from = `${y}-01-01`;
    to = `${y}-12-31`;
  } else if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    from = toLocalDateString(new Date(date[0]));
    to = toLocalDateString(new Date(date[1]));
  } else {
    return { from: null, to: null };
  }
  // Obmedzenie na súčasnosť: nepočítame budúce dni ani dnešok
  if (to && to > lastDayStr) to = lastDayStr;
  if (from && to && from > to) return { from: lastDayStr, to: lastDayStr };
  return { from, to };
}

export default function PreviewChecklists() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { t, locale } = useContext(LocaleContext);
    const periodOptionLabels = useMemo(() => buildPreviewPeriodOptionLabels(t), [t]);
    const [calendarLocale, setCalendarLocale] = useState(() => registerPreviewCalendarLocale(addLocale, locale, t));
    useEffect(() => { setCalendarLocale(registerPreviewCalendarLocale(addLocale, locale, t)); }, [locale, t]);
  const { setUserMaskFilter } = useContext(UtilContext);
  const [timePeriod, setTimePeriod] = useState('Za mesiac');
  const [date, setDate] = useState(() => new Date());
  const [dateProps, setDateProps] = useState({ view: 'month', dateFormat: 'mm.yy' });
  const appliedSavedRef = useRef(false);

  // Načítať uložené obdobie z DB (user_mask_filters) pri prvom dostupnom maske; neoznačiť ref kým nemáme masky, aby sa pri neskoršom načítaní maska aplikovala
  useEffect(() => {
    if (appliedSavedRef.current) return;
    if (!state.user_mask_filters?.length) return;
    appliedSavedRef.current = true;
    const mask = state.user_mask_filters.find((m) => m.page === PREVIEW_PAGE);
    const attrs = mask?.attributes;
    const parsed = typeof attrs === 'string' ? (() => { try { return parseSavedPeriod(JSON.parse(attrs)); } catch { return null; } })() : parseSavedPeriod(attrs);
    if (parsed) {
      setTimePeriod(parsed.timePeriod);
      setDate(parsed.date ?? (parsed.timePeriod === 'Všetky' ? null : new Date()));
      if (parsed.timePeriod === 'Za deň') setDateProps({ dateFormat: 'dd.mm.yy' });
      else if (parsed.timePeriod === 'Za mesiac') setDateProps({ view: 'month', dateFormat: 'mm.yy' });
      else if (parsed.timePeriod === 'Za rok') setDateProps({ view: 'year', dateFormat: 'yy' });
      else if (parsed.timePeriod === 'Vlastné') setDateProps({ dateFormat: 'dd.mm.yy', selectionMode: 'range' });
      else setDateProps({ hidden: true });
    }
  }, [state.user_mask_filters]);
  const [stats, setStats] = useState({ moje: null, podriadenych: null, vyjadrenia: null, uplny: null });
  const [loading, setLoading] = useState(false);
  const [legendHidden, setLegendHidden] = useState({ moje: [], podriadenych: [], vyjadrenia: [], uplny: [] });
  const originalDataRef = useRef({ moje: [], podriadenych: [], vyjadrenia: [], uplny: [] });
  const mojeChartRef = useRef(null);
  const podriadenychChartRef = useRef(null);
  const vyjadreniaChartRef = useRef(null);
  const uplnyChartRef = useRef(null);

  const canAccessChecklists = user?.permissions?.includes('access checklists');
  const canEditChecklists = user?.permissions?.includes('edit checklists');
  const canAccessComplete = user?.permissions?.includes('access checklists-complete-list') || user?.is_superuser;

  const fetchStats = (from, to) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('from_date', from);
    if (to) params.set('to_date', to);
    getRequest(`/api/checklist_preview_stats?${params.toString()}`)
      .then((resp) => {
        setStats(resp?.data ?? { moje: null, podriadenych: null, vyjadrenia: null, uplny: null });
      })
      .catch(() => setStats({ moje: null, podriadenych: null, vyjadrenia: null, uplny: null }))
      .finally(() => setLoading(false));
  };

  // Uložiť zvolené obdobie do DB (user_mask_filters) pre aktuálneho používateľa
  const savePeriodToDb = () => {
    setUserMaskFilter(PREVIEW_PAGE, 'set_attributes', null, null, { preview_period: serializePeriod(timePeriod, date) });
  };

  // Refetch when user changes (po prihlásení iného používateľa nesmú ostávať staré čísla)
  useEffect(() => {
    if (!user) {
      setStats({ moje: null, podriadenych: null, vyjadrenia: null, uplny: null });
      return;
    }
    if (timePeriod === 'Všetky' || !date) {
      fetchStats(null, null);
    } else {
      const { from, to } = getDateRange(timePeriod, date);
      fetchStats(from, to);
    }
  }, [timePeriod, date, user?.id]);

  useEffect(() => {
    setLegendHidden({ moje: [], podriadenych: [], vyjadrenia: [], uplny: [] });
    originalDataRef.current = {
      moje: stats.moje ? [stats.moje.ok, stats.moje.nok, stats.moje.rozpracovane, stats.moje.nevytvoreny] : [],
      podriadenych: stats.podriadenych ? [stats.podriadenych.ok, stats.podriadenych.nok, stats.podriadenych.rozpracovane, stats.podriadenych.nevytvoreny] : [],
      vyjadrenia: stats.vyjadrenia
        ? [
            (stats.vyjadrenia.otvorene ?? 0) - (stats.vyjadrenia.otvorene_po_terminie ?? 0),
            stats.vyjadrenia.otvorene_po_terminie ?? 0,
            stats.vyjadrenia.uzavrete ?? 0,
          ]
        : [],
      uplny: stats.uplny ? [stats.uplny.ok, stats.uplny.nok, stats.uplny.rozpracovane, stats.uplny.nevytvoreny] : [],
    };
  }, [stats]);

  function changeTimePeriod(time_period) {
    setTimePeriod(time_period);
    setDate(null);
    if (time_period === 'Za deň') setDateProps({ dateFormat: 'dd.mm.yy' });
    else if (time_period === 'Za mesiac') setDateProps({ view: 'month', dateFormat: 'mm.yy' });
    else if (time_period === 'Za rok') setDateProps({ view: 'year', dateFormat: 'yy' });
    else if (time_period === 'Vlastné') setDateProps({ dateFormat: 'dd.mm.yy', selectionMode: 'range' });
    else setDateProps({ hidden: true });
  }

  function showResult(newDate) {
    setDate(newDate);
  }

  function handleRefresh() {
    if (timePeriod === 'Všetky' || !date) {
      fetchStats(null, null);
    } else {
      const { from, to } = getDateRange(timePeriod, date);
      fetchStats(from, to);
    }
  }

  const plugin = {
    id: 'emptyDoughnut',
    afterDraw(chart) {
      const data = chart.data?.datasets?.[0]?.data ?? [];
      const count = data.reduce((a, b) => a + (b ?? 0), 0);
      if (count < 1) {
        const ctx = chart.ctx;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '20px Arial';
        ctx.fillStyle = '#9ca3af';
        ctx.fillText(t('Common.NoRecords'), chart.width / 2, chart.height / 2);
      }
    },
  };

  const openList = (page, titleKey, mask, preview) => {
    dispatch({
      type: 'create_new_tab',
      name: t(titleKey),
      translationKey: titleKey,
      legacyLabel: t(titleKey),
      page,
      props: { preview, timePeriod, date },
      mask,
    });
  };

  // Celkový počet podľa viditeľných výsekov (pri vypnutí v legende sa číslo zmení)
  const originalTotal = (chartKey, fallbackArr) => {
    const hidden = legendHidden[chartKey] ?? [];
    const originals = originalDataRef.current[chartKey];
    if (Array.isArray(originals)) {
      return originals.reduce((acc, val, i) => acc + (hidden[i] ? 0 : (Number(val) || 0)), 0);
    }
    if (fallbackArr && fallbackArr.length) {
      return fallbackArr.reduce((a, b) => a + (Number(b) || 0), 0);
    }
    return 0;
  };

  const toggleLegendItem = (chartKey, index, chartRef) => () => {
    const chartApi = chartRef?.current;
    const ch = chartApi?.getChart?.();
    if (!ch) return;

    // základné dáta – ak by z nejakého dôvodu neboli v refe, vezmeme ich priamo z grafu
    let originals = originalDataRef.current[chartKey];
    if (!Array.isArray(originals) || originals.length === 0) {
      const src = ch.data?.datasets?.[0]?.data;
      if (!Array.isArray(src)) return;
      originals = src.map((v) => Number(v) || 0);
      originalDataRef.current[chartKey] = originals;
    }
    const currentlyHidden = legendHidden[chartKey]?.[index];
    const nextHidden = !currentlyHidden;
    const hiddenArr = legendHidden[chartKey] ?? [];
    const newData = originals.map((val, i) => {
      const isHidden = i === index ? nextHidden : hiddenArr[i];
      return isHidden ? 0 : (Number(val) || 0);
    });
    const ds = ch.data.datasets[0];
    ds.data = [...newData];
    const meta = ch.getDatasetMeta?.(0);
    if (meta && Array.isArray(meta._parsed)) newData.forEach((v, i) => { meta._parsed[i] = v; });
    ch.update();
    setLegendHidden((prev) => {
      const arr = prev[chartKey] ?? [];
      const next = [...arr];
      next[index] = nextHidden;
      return { ...prev, [chartKey]: next };
    });
  };

  const chartStatusColors = getChecklistGraphStatusColors(state.module_settings || []);
  const chartVyjadreniaColors = getChecklistGraphVyjadreniaColors(state.module_settings || []);

  const mojeData = useMemo(() => (
    stats.moje && {
      labels: ['OK', 'NOK', t('Status.InProgressWork'), t('Status.NotCreated')],
      datasets: [{
        data: [stats.moje.ok, stats.moje.nok, stats.moje.rozpracovane, stats.moje.nevytvoreny],
        backgroundColor: chartStatusColors,
        hoverBackgroundColor: chartStatusColors,
      }],
    }
  ), [stats.moje, chartStatusColors]);

  const mojeOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'ok', 1: 'nok', 2: 'rozpracovane', 3: 'nevytvoreny' };
      openList('ChecklistRecordMyList', 'Checklist.List', 'chck009', map[item[0].index]);
    },
  };

  const podriadenychData = useMemo(() => (
    stats.podriadenych && {
      labels: ['OK', 'NOK', t('Status.InProgressWork'), t('Status.NotCreated')],
      datasets: [{
        data: [stats.podriadenych.ok, stats.podriadenych.nok, stats.podriadenych.rozpracovane, stats.podriadenych.nevytvoreny],
        backgroundColor: chartStatusColors,
        hoverBackgroundColor: chartStatusColors,
      }],
    }
  ), [stats.podriadenych, chartStatusColors]);

  const podriadenychOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'ok', 1: 'nok', 2: 'rozpracovane', 3: 'nevytvoreny' };
      openList('ChecklistStateList', 'Checklist.ManagerResponses', 'chck005', map[item[0].index]);
    },
  };

  const vyjadreniaData = useMemo(() => (
    stats.vyjadrenia && {
      labels: [t('Status.OpenPlural'), t('Common.OpenOverdue'), t('Status.ClosedPlural')],
      datasets: [{
        data: [
          (stats.vyjadrenia.otvorene ?? 0) - (stats.vyjadrenia.otvorene_po_terminie ?? 0),
          stats.vyjadrenia.otvorene_po_terminie ?? 0,
          stats.vyjadrenia.uzavrete ?? 0,
        ],
        backgroundColor: chartVyjadreniaColors,
        hoverBackgroundColor: chartVyjadreniaColors,
      }],
    }
  ), [stats.vyjadrenia, chartVyjadreniaColors]);

  const vyjadreniaOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'otvorene', 1: 'otvorene_po_terminie', 2: 'uzavrete' };
      openList('ChecklistStateList', 'Checklist.ManagerResponses', 'chck005', map[item[0].index]);
    },
  };

  const uplnyData = useMemo(() => (
    stats.uplny && {
      labels: ['OK', 'NOK', t('Status.InProgressWork'), t('Status.NotCreated')],
      datasets: [{
        data: [stats.uplny.ok ?? 0, stats.uplny.nok ?? 0, stats.uplny.rozpracovane ?? 0, stats.uplny.nevytvoreny ?? 0],
        backgroundColor: chartStatusColors,
        hoverBackgroundColor: chartStatusColors,
      }],
    }
  ), [stats.uplny, chartStatusColors]);

  const uplnyOptions = {
    plugins: { legend: { display: false } },
    onClick(evt, item) {
      if (!item?.length) return;
      const map = { 0: 'ok', 1: 'nok', 2: 'rozpracovane', 3: 'nevytvoreny' };
      openList('ChecklistRecordAllList', 'Common.FullList', 'chck011', map[item[0].index]);
    },
  };

  useEffect(() => {
    const d = (src) => (src?.datasets?.[0]?.data ? [...src.datasets[0].data] : []);
    originalDataRef.current = {
      moje: d(mojeData),
      podriadenych: d(podriadenychData),
      vyjadrenia: d(vyjadreniaData),
      uplny: d(uplnyData),
    };
  }, [mojeData, podriadenychData, vyjadreniaData, uplnyData]);

  return (
    <div className="h-[calc(100vh-137px)] overflow-y-auto">
      <div className="lg:grid grid-cols-3 gap-1">
        <div />
        <div>
          <div className="flex items-center gap-3 mt-4">
            <label htmlFor="time_period" className="text-xl font-semibold text-gray-800 shrink-0">{t('Common.PeriodLabel')}</label>
            <select
              name="time_period"
              value={timePeriod}
              onChange={(e) => changeTimePeriod(e.target.value)}
              className="bg-white flex-1 min-w-0 px-4 py-2 text-gray-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            >
              <option value="Všetky">{periodOptionLabels['Všetko']}</option>
              <option value="Za deň">{periodOptionLabels['Za deň']}</option>
              <option value="Za mesiac">{periodOptionLabels['Za mesiac']}</option>
              <option value="Za rok">{periodOptionLabels['Za rok']}</option>
              <option value="Vlastné">{periodOptionLabels['Vlastné']}</option>
            </select>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              title={t('Common.Refresh')}
              className="flex items-center justify-center w-10 h-10 shrink-0 rounded-md border border-gray-300 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none text-white transition-colors"
            >
              <RefreshIcon />
            </button>
            <button
              type="button"
              onClick={savePeriodToDb}
              title={t('PreviewChecklists.SavePeriodTitle')}
              className="shrink-0 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring focus:ring-gray-200"
            >
              {t('Common.SaveMask')}
            </button>
          </div>
        </div>
        <div />
        <div />
        <div className="flex-auto">
          <Calendar
            value={date}
            onChange={(e) => showResult(e.target.value)}
            showIcon
            className="w-full"
            locale={calendarLocale}
            {...dateProps}
            hideOnRangeSelection
            style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }}
          />
        </div>
        <div />
      </div>

      {loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
              <div className="text-center mb-4"><Skeleton width="12rem" height="1.5rem" className="mx-auto" /></div>
              <div className="w-full max-w-[420px] h-[420px] mx-auto flex items-center justify-center">
                <Skeleton shape="circle" size="320px" className="flex-shrink-0" />
              </div>
              <div className="text-center mt-4"><Skeleton width="8rem" height="1.5rem" className="mx-auto" /></div>
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
            {canAccessChecklists && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewChecklists.My')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={mojeChartRef}
                    type="doughnut"
                    data={mojeData || { labels: [], datasets: [{ data: [] }] }}
                    options={mojeOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                  {(mojeData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('moje', i, mojeChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{ textDecoration: legendHidden.moje?.[i] ? 'line-through' : undefined, opacity: legendHidden.moje?.[i] ? 0.5 : 1 }}
                    >
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: mojeData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  Celkom: {originalTotal('moje', mojeData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}

            {canEditChecklists && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewChecklists.Subordinates')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={podriadenychChartRef}
                    type="doughnut"
                    data={podriadenychData || { labels: [], datasets: [{ data: [] }] }}
                    options={podriadenychOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                  {(podriadenychData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('podriadenych', i, podriadenychChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{ textDecoration: legendHidden.podriadenych?.[i] ? 'line-through' : undefined, opacity: legendHidden.podriadenych?.[i] ? 0.5 : 1 }}
                    >
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: podriadenychData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  Celkom: {originalTotal('podriadenych', podriadenychData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}

            {canEditChecklists && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewChecklists.ManagerResponses')}</h2>
              <div className="w-full max-w-[420px] h-[420px] mx-auto">
                <Chart
                  ref={vyjadreniaChartRef}
                  type="doughnut"
                  data={vyjadreniaData || { labels: [], datasets: [{ data: [] }] }}
                  options={vyjadreniaOptions}
                  className="w-full h-full"
                  plugins={[plugin]}
                />
              </div>
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                {(vyjadreniaData?.labels ?? []).map((label, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={toggleLegendItem('vyjadrenia', i, vyjadreniaChartRef)}
                    className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                    style={{ textDecoration: legendHidden.vyjadrenia?.[i] ? 'line-through' : undefined, opacity: legendHidden.vyjadrenia?.[i] ? 0.5 : 1 }}
                  >
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: vyjadreniaData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-center font-semibold mt-4">
                Celkom: {originalTotal('vyjadrenia', vyjadreniaData?.datasets?.[0]?.data)}
              </div>
            </div>
          )}

            {/* Úplný zoznam – v ďalšom riadku (rovnako ako Dokumentácie v preview OTK) */}
            {canAccessComplete && (
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 rounded-xl shadow-md p-4">
                <h2 className="text-center text-xl font-semibold mb-4">{t('PreviewChecklists.FullList')}</h2>
                <div className="w-full max-w-[420px] h-[420px] mx-auto">
                  <Chart
                    ref={uplnyChartRef}
                    type="doughnut"
                    data={uplnyData || { labels: [], datasets: [{ data: [] }] }}
                    options={uplnyOptions}
                    className="w-full h-full"
                    plugins={[plugin]}
                  />
                </div>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                  {(uplnyData?.labels ?? []).map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={toggleLegendItem('uplny', i, uplnyChartRef)}
                      className="inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 text-left hover:opacity-80"
                      style={{ textDecoration: legendHidden.uplny?.[i] ? 'line-through' : undefined, opacity: legendHidden.uplny?.[i] ? 0.5 : 1 }}
                    >
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: uplnyData?.datasets?.[0]?.backgroundColor?.[i] ?? '#6b7280' }} />
                      {label}
                    </button>
                  ))}
                </div>
                <div className="text-center font-semibold mt-4">
                  Celkom: {originalTotal('uplny', uplnyData?.datasets?.[0]?.data)}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
